import { ValidationResult, useSprintMutation, SprintMutationResult, useSprintQueryClient } from "sprint-common-web";

import { UpdateOnPremAssetContentResponse } from "../../../../../../common/api/master-api/assets/details/UpdateOnPremAssetContentResponse";
import { ValidateOnPremAssetContentRequest } from "../../../../../../common/api/master-api/assets/details/ValidateOnPremAssetContentRequest";
import { useAssetsApi } from "../../../../../../common/api/master-api/assets/useAssetsApi";
import { OnPremAssetQueryKey } from "../../../common/OnPremAssetQueryKey";

export interface UseEditOnPremAsset {
    validateOnPremAssetContent: SprintMutationResult<ValidateOnPremAssetContentRequest, ValidationResult>;
    updateOnPremAssetContent: SprintMutationResult<ValidateOnPremAssetContentRequest, UpdateOnPremAssetContentResponse>;
}

export const useEditOnPremAssetContent = ({ invalidationKey }: { invalidationKey?: string }): UseEditOnPremAsset => {
    const assetsApi = useAssetsApi();
    const sprintQueryClient = useSprintQueryClient();

    const validateOnPremAssetContent = useSprintMutation<ValidateOnPremAssetContentRequest, ValidationResult>({
        mutationFn: async ({ assetId, contentReference }): Promise<ValidationResult> => {
            if (!assetId) {
                throw new Error("Asset ID is required to validate on-prem asset content.");
            }

            return assetsApi.validateOnPremAssetContent({ assetId, contentReference });
        }
    });

    const updateOnPremAssetContent = useSprintMutation<
        ValidateOnPremAssetContentRequest,
        UpdateOnPremAssetContentResponse
    >({
        mutationFn: async ({ assetId, contentReference }: ValidateOnPremAssetContentRequest) => {
            if (!assetId) {
                throw new Error("Asset ID is required to validate on-prem asset content.");
            }

            return assetsApi.updateOnPremAssetContent({ assetId, contentReference });
        },
        onSettled: async () => {
            await sprintQueryClient.invalidateQueries({
                queryKey: [OnPremAssetQueryKey.OnPremAssetDetails, invalidationKey]
            });
        }
    });

    return {
        validateOnPremAssetContent,
        updateOnPremAssetContent
    };
};
